<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import CompanySetupModal from '@/components/authentication/company-setup/CompanySetupModal.vue'
import { useAuthStore } from '@/stores/authStore.js'
import { createCorporation, getUser, logout } from '@/services/api/auth.js'
import {
    createDatabase,
    getCorp,
    getCorpById,
    makePermissionStructure,
    setCorp,
    updateCorp
} from '@/services/api/corporation.js'
import {
    addServiceToCorp,
    getFirstServiceId,
    getServicesByName,
    setMePermissionsManagement
} from '@/services/api/permissionManagement.js'
import message from '@/services/messages.js'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const authStore = useAuthStore()
const showCompanySetup = ref(true)
const stage = ref('form')
const provisionMessageIndex = ref(0)
const provisionInterval = ref(null)
const currentCorpId = ref(null)
const companyData = ref({})
const isEditMode = computed(() => route.query.mode === 'edit')

const provisionMessages = computed(() => [
    t('components.authentication.companySetup.provisioning.creating'),
    t('components.authentication.companySetup.provisioning.preparing'),
    t('components.authentication.companySetup.provisioning.details'),
    t('components.authentication.companySetup.provisioning.almost'),
    t('components.authentication.companySetup.provisioning.finishing'),
])

const selectedProvisionMessage = computed(() => {
    return provisionMessages.value[provisionMessageIndex.value]
})
const readyTitle = computed(() => {
    return isEditMode.value
        ? t('components.authentication.companySetup.updatedTitle')
        : t('components.authentication.companySetup.readyTitle')
})
const readySubtitle = computed(() => {
    return isEditMode.value
        ? t('components.authentication.companySetup.updatedSubtitle')
        : t('components.authentication.companySetup.readySubtitle')
})

const startProvisioningMessages = () => {
    if (provisionInterval.value) {
        return
    }

    provisionMessageIndex.value = 0
    provisionInterval.value = window.setInterval(() => {
        provisionMessageIndex.value = (provisionMessageIndex.value + 1) % provisionMessages.value.length
    }, 2000)
}

const stopProvisioningMessages = () => {
    if (!provisionInterval.value) {
        return
    }

    window.clearInterval(provisionInterval.value)
    provisionInterval.value = null
    provisionMessageIndex.value = 0
}

const getCreatedCorpId = (response) => {
    return response?.id
        || response?.data?.id
        || response?.corp?.id
        || response?.data?.corp?.id
        || response?.credentials?.corp
}

const addServiceByNameToCorp = async ({ corpId, name }) => {
    const servicesResponse = await getServicesByName({ name })
    const serviceId = getFirstServiceId(servicesResponse)

    if (!serviceId) {
        throw new Error(`Service ${name} not found`)
    }

    await addServiceToCorp({ corpId, serviceId })
}

const normalizeCorpResponse = (response = {}) => {
    return response?.data?.corp || response?.data || response
}

const getUserCorpsCount = () => {
    const user = getUser()
    const corps = user?.fullUser?.user?.corps || user?.fullUser?.corps || {}

    return Object.keys(corps).length
}

const getSettingsBackRoute = () => {
    return route.path.includes('/rhdigital') ? '/rhdigital/settings' : '/settings'
}

const goBackFromFirstStep = async () => {
    if (isEditMode.value || getUserCorpsCount() > 0) {
        router.push(getSettingsBackRoute())
        return
    }

    const signInRoute = authStore.signInRoute || '/'
    await logout()
    router.push(signInRoute)
}

const loadCompanyToEdit = async () => {
    if (!isEditMode.value) {
        return
    }

    try {
        const corp = await getCorp()
        currentCorpId.value = corp?.id

        if (!currentCorpId.value) {
            throw new Error('Current corporation id not found')
        }

        const response = await getCorpById({ id: currentCorpId.value })
        companyData.value = normalizeCorpResponse(response)
    } catch (error) {
        message.error(t('components.authentication.companySetup.validation.loadCompany'))
        router.push(route.query.redirect || '/settings')
    }
}

const updateCompany = async (data) => {
    showCompanySetup.value = false
    stage.value = 'provisioning'
    startProvisioningMessages()

    try {
        if (!currentCorpId.value) {
            throw new Error('Current corporation id not found')
        }

        await updateCorp({
            id: currentCorpId.value,
            auth_name: data.authName,
            company_name: data.companyName,
            business_name: data.businessName,
            image_content: data.imageContent,
        })

        await setMePermissionsManagement({ id: getUser()?.id })
        stage.value = 'ready'
    } catch (error) {
        message.error(t('components.authentication.companySetup.validation.updateCompany'))
        showCompanySetup.value = true
        stage.value = 'form'
    } finally {
        stopProvisioningMessages()
    }
}

const createCompany = async (companyData) => {
    if (isEditMode.value) {
        await updateCompany(companyData)
        return
    }

    showCompanySetup.value = false
    stage.value = 'provisioning'
    startProvisioningMessages()

    try {
        const response = await createCorporation(companyData)
        const corpId = getCreatedCorpId(response)

        if (!corpId) {
            throw new Error('Created corporation id not found')
        }

        await createDatabase()
        await setCorp({ id: corpId })
        await makePermissionStructure()

        await addServiceByNameToCorp({ corpId, name: 'hliberty.admin' })
        await addServiceByNameToCorp({ corpId, name: 'hliberty.corp' })
        await setMePermissionsManagement({id:  getUser()?.id})
        stage.value = 'ready'
    } catch (error) {
        message.error(t('components.authentication.companySetup.validation.createCompany'))
        showCompanySetup.value = true
        stage.value = 'form'
    } finally {
        stopProvisioningMessages()
    }
}

const continueAfterCompanySetup = () => {
    const redirectRoute = route.query.redirect || authStore.companySetupRedirectRoute || '/home'

    authStore.clearCompanySetupRedirectRoute()
    router.push(redirectRoute)
}

onMounted(loadCompanyToEdit)

onBeforeUnmount(() => {
    stopProvisioningMessages()
})
</script>

<template>
    <div class="company-setup-page bg-body-bg">
        <CompanySetupModal
            v-if="stage === 'form'"
            v-model="showCompanySetup"
            :mode="isEditMode ? 'edit' : 'create'"
            :initial-data="companyData"
            @created="createCompany"
            @back="goBackFromFirstStep"
        />

        <section v-else-if="stage === 'provisioning'" class="company-setup-progress">
            <div class="company-setup-progress__loader" aria-hidden="true">
                <span>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none"><path data-v-b0991ab5="" d="M2.2 15.8
                    C5.2 15.2 6.4 9.2 10.2 7.2
                    C13.2 5.6 17.2 6.4 18.8 9.1
                    C20 11.1 19.2 13.5 17.2 14.2
                    C15.6 14.8 14.4 13.8 14.8 12.6
                    C15.2 11.4 16.4 11.4 17.2 12
                    C16.6 10.1 14.1 9.6 12.4 10.6
                    C10.2 11.9 10.3 15.3 12.6 16.6
                    C15.3 18.2 18.2 15.4 21.8 15.8
                    C19.5 18.6 16.4 20 13.6 19.5
                    C10.8 19 9.3 16.7 7.2 16.2
                    C5.4 15.8 4 17.3 2.2 17.4
                    Z" fill="currentColor"></path></svg>
                </span>
            </div>

            <h1>{{ selectedProvisionMessage }}</h1>
        </section>

        <section v-else class="company-setup-progress company-setup-progress--ready">
            <span class="company-setup-progress__celebration material-symbols-outlined">celebration</span>
            <h1>{{ readyTitle }}</h1>
            <p>{{ readySubtitle }}</p>
            <v-btn class="company-setup-progress__button" @click="continueAfterCompanySetup">
                {{ $t('components.authentication.companySetup.continue') }}
            </v-btn>
        </section>
    </div>
</template>

<style lang="scss" scoped>
.company-setup-page {
    display: grid;
    place-items: center;
    min-height: 100vh;
    padding: 24px;
}

.company-setup-progress {
    display: grid;
    justify-items: center;
    gap: 24px;
    width: min(100%, 620px);
    padding: 40px 24px;
    text-align: center;

    h1 {
        margin: 0;
        color: var(--blackColor);
        font-family: Inter, sans-serif;
        font-size: 30px;
        font-weight: 850;
        line-height: 1.25;
    }

    p {
        max-width: 360px;
        margin: -12px 0 0;
        color: var(--bodyColor);
        font-size: 15px;
        line-height: 1.6;
    }

    &__logo {
        display: inline-flex;
        align-items: center;
        gap: 12px;
        padding: 10px 14px;
        border: 1px solid #eceef2;
        border-radius: 16px;
        background: #fff;
        box-shadow: 0 10px 28px rgba(96, 93, 255, 0.1);

        span {
            display: grid;
            place-items: center;
            width: 42px;
            height: 42px;
            border-radius: 12px;
            background: linear-gradient(128deg, #8b8b8b 0%, #fffefe 100%);
        }

        img {
            width: 26px;
            height: 26px;
            object-fit: contain;
        }

        strong {
            color: #302424;
            font-size: 16px;
            font-weight: 850;
        }
    }

    &__loader {
        position: relative;
        display: grid;
        place-items: center;
        width: 104px;
        height: 104px;
        border-radius: 50%;
        background: var(--whiteColor);
        box-shadow: 0 18px 52px rgba(96, 93, 255, 0.14);

        &::before {
            content: '';
            position: absolute;
            inset: 0;
            border: 4px solid rgba(96, 93, 255, 0.1);
            border-top-color: #ffd056;
            border-radius: 50%;
            animation: company-spin 0.9s linear infinite;
        }

        span {
            border-radius: 15px;
        }
    }

    &__celebration {
        display: grid;
        place-items: center;
        width: 92px;
        height: 92px;
        border-radius: 28px;
        background: linear-gradient(135deg, #ffd056 0%, #ffe7389e 100%);
        color: #302424;
        font-size: 46px;
        box-shadow: 0 18px 52px rgba(255, 208, 86, 0.22);
    }

    &__button {
        min-height: 46px;
        border-radius: 10px;
        background: linear-gradient(135deg, #ffd056 0%, #ffe7389e 100%) !important;
        color: #2c2c2cd0 !important;
        font-family: Inter, sans-serif;
        font-weight: 850;
        letter-spacing: 0;
        text-transform: none;
    }
}

:global(.dark-theme) {
    .company-setup-progress {
        h1 {
            color: var(--whiteColor);
        }

        &__loader {
            background: #0c1427;
            box-shadow: 0 18px 52px rgba(0, 0, 0, 0.32);
        }
    }
}

@keyframes company-spin {
    to {
        transform: rotate(360deg);
    }
}

@media only screen and (max-width: 720px) {
    .company-setup-progress {
        h1 {
            font-size: 24px;
        }
    }
}
</style>
